import { Product } from "@/api/product-api";
import type { CartTrigger } from "@/libs/analytics/cart-trigger";
import { useAddItem } from "@/store/cart-store";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { SelectProductOption } from "@/v2-components/cart-sheet/SelectProductOption";
import { SelectRecommendedBundle } from "@/v2-components/cart-sheet/SelectRecommendedBundle";
import { useRouter } from "next/router";
import { useAddedToBagStore } from "@/store/cart-store/useAddedToBagStore";
import { AddToCartResult } from "@/store/cart-store/type";
import { useState } from "react";

// - 제품 추가시 번들옵션이나 제품옵션이 있는 경우 선택하는 훅.
// - 기존에 카트시트에서 카트스토어의 상태에 따라 제품옵션 선택시트나 번들선택 시트를
// 보여주던 것을 훅으로 분리
// - 제품 추가시 필요한 로직을 담당
export const useAddItemToCart = (
  /**
   * `add_to_cart` 의 진입점 (#5292). **버튼이 놓인 표면이 정한다.**
   *
   * ⚠️ 이 훅 하나를 `ProductCard`·`TactSuitCard`·카로셀·스펙 카드(=제품 카드)와
   * `/cart` 추천 레일이 **함께** 쓴다. 훅 안에서 값을 못 박으면 한쪽이 반드시 틀린다 —
   * `entryPoint` 를 호출처가 정하게 한 것과 같은 이유다.
   */
  trigger: CartTrigger = "unknown",
  /**
   * 담기 완료 팝(#5366)을 띄울 표면인가. **표면이 정한다.**
   *
   * ⚠️ 여기서 `trigger` 값으로 갈라선 안 된다 — `cart-trigger-threading` 가드가
   * *"trigger 값은 표면에서만 만든다"* 를 소스 스캔으로 잠근다(공유 훅이 값을 알면
   * 여러 표면 중 한쪽이 반드시 틀린다, #5292 에서 두 번 났다). 실제로 처음엔
   * `trigger !== "cart_sheet"` 로 썼고 그 가드가 잡았다.
   *
   * 기본값이 **`true`** 인 이유: 고치는 결함이 「담았는데 아무 신호가 없다」이므로,
   * 새 표면이 생겼을 때 조용히 무음으로 돌아가는 쪽이 더 나쁘다. 끄는 표면만 명시한다.
   */
  notifyOnAdd = true,
) => {
  const router = useRouter();
  const { addCartBySlug } = useCartWithProducts();
  const addItem = useAddItem();
  const openAddedToBag = useAddedToBagStore((s) => s.open);
  const [recommendedBundles, setRecommendedBundles] = useState<Product[]>([]);

  const addItemToCart = (slug: string) => {
    const result = addCartBySlug(slug, trigger);
    // ⚠️ `redirectTo` 를 **먼저** 본다. 그 갈래는 `bundles: []` 이라 순서를 뒤집으면
    //    조용히 아무 일도 안 일어난다(게임키 번들이 다시 무음으로 실패한다, #5286).
    //    라우터를 아는 곳은 여기 하나다 — `useCartWithProducts` 는 소비처가 23곳이라
    //    거기서 `useRouter()` 를 부르면 전부 라우터 컨텍스트에 묶인다.
    if (result?.redirectTo) {
      router.push(result.redirectTo);
      return;
    }
    if (result?.bundles.length) {
      setRecommendedBundles(result.bundles);
      return;
    }
    // ★여기까지 오면 **담기가 끝났다**(`addCartBySlug` 가 `addItem` 을 호출한 갈래).
    //
    // 종전에는 **아무 일도 일어나지 않았다** — 담겼는데 화면에 신호가 없어서 사용자가 담긴
    // 줄 모른다(이슈 #5366: *"악세사리 장바구니에 추가누르면 팝업이 안뜹니다"*). 옵션·번들이
    // 있는 제품은 시트가 떠서 이 구멍이 안 보였다.
    //
    // ⚠️ **어느 표면에서 띄우는지는 표면이 정한다**(`notifyOnAdd`). 이 훅은 제품 카드
    // 네 곳과 카트 추천 레일이 함께 쓰는데, 카트 화면에서는 담은 결과가 **그 자리에 바로
    // 보인다** — 거기서 헤더 아이콘 아래 팝을 띄우면 같은 정보를 두 번 말하고 목록을 가린다.
    //
    // 담은 SKU 를 넘긴다: 팝이 카트 전체가 아니라 **방금 담은 항목**을 보여준다.
    // 옵션 없는 제품만 이 갈래로 오므로 SKU 는 `skuOptions[0]` 하나로 확정된다.
    if (notifyOnAdd && result?.addedSku) {
      openAddedToBag(result.addedSku);
    }
  };

  const sheets = (
    <>
      {recommendedBundles.length > 1 && (
        <SelectRecommendedBundle
          trigger={trigger}
          notifyOnAdd={notifyOnAdd}
          onContinue={(product) => {
            // 추천 번들 시트에서 담기 — 진입은 제품 카드다 (#5292)
            const result = addItem(product, true, trigger);
            setRecommendedBundles([]);
            // ★이 자리도 종전에 **무음**이었다 (#5366 리뷰 R1 → `addItem` 호출 자리
            // 전수로 확인). 옵션 시트와 같은 판정 — `Success` 만. `OverLimit`·
            // `MergedToBundle` 은 이미 자기 표면(Purchase Limit 다이얼로그 · `/cart`
            // 이동)이 있어서 팝을 겹치면 사실과 다른 말을 한다.
            if (notifyOnAdd && result === AddToCartResult.Success) {
              openAddedToBag(product.sku);
            }
          }}
          onClose={() => setRecommendedBundles([])}
          products={recommendedBundles}
        />
      )}
      {recommendedBundles.length === 1 && (
        <SelectProductOption
          product={recommendedBundles[0]}
          onClose={() => setRecommendedBundles([])}
          trigger={trigger}
          notifyOnAdd={notifyOnAdd}
        />
      )}
    </>
  );

  return { addItemToCart, sheets };
};
